import { INK, seriesColour } from './Chart';

/**
 * One figure against bands of good and bad: "how many months does the cash
 * last" on a scale whose red, amber and green say what the number means.
 *
 * ── A BAR, NOT A DIAL ──
 * A semicircle gauge spends half its area on decoration and makes a reader
 * judge an angle; a straight scale is read by position, which people judge
 * well, and fits a stat tile. The bands are tinted, the marker is the ink,
 * and the scale's end is labelled with a plus because the figure can run
 * past it - eighteen months of cash is drawn at the end, and said in full
 * in the words beside it.
 */

export interface MeterProps {
  value: number | null;
  /** The scale's end. */
  max: number;
  /** Ascending upper edges with their colours, the last ending at `max`: [[3, 'red'], [6, 'amber'], [12, 'green']]. */
  bands: Array<[number, string]>;
  /** What the figure is, said whole: "0.2 months if nothing comes in". */
  label: string;
  /** The scale's unit for its end label: "mo". */
  unit: string;
}

export function Meter({ value, max, bands, label, unit }: MeterProps) {
  const at = value === null ? null : Math.max(0, Math.min(max, value));
  return (
    <div role="img" aria-label={label} data-chart-meter>
      <svg width="100%" height={22} viewBox="0 0 100 22" preserveAspectRatio="none" className="block">
        {bands.map(([to, colour], i) => {
          const from = i === 0 ? 0 : (bands[i - 1]?.[0] ?? 0);
          return <rect key={to} x={(from / max) * 100} y={6} width={((to - from) / max) * 100} height={8} fill={seriesColour(colour)} opacity={0.3} />;
        })}
        {at !== null ? <rect x={Math.min(99, (at / max) * 100)} y={2} width={1.2} height={16} fill={INK.text} /> : null}
      </svg>
      <div className="mt-0.5 flex justify-between text-[10px] text-muted-foreground tabular-nums" aria-hidden>
        <span>0</span>
        <span>
          {max}+ {unit}
        </span>
      </div>
    </div>
  );
}
